import { useState } from "react"
import { t } from "@lingui/core/macro"
import {
    FollowNotificationFrequency,
    type IFollowing,
} from "../../../types/follow"
import { parseFollowNotificationFrequency } from "../../../utils/followUtils"
import { addPageAction } from "../../../utils/newrelic"
import { ClickableTooltip } from "../../common/molecules/ClickableTooltip"
import { Placement } from "../../common/molecules/ClickableTooltip/ClickableTooltip"
import { FollowStar } from "../../common/molecules/FollowStar/FollowStar"
import { Position, Preset } from "../common/Toast/Toast"
import { GLOBAL_ID, toast } from "../common/Toast/ToastManager"
import { useMessagingContext } from "../MessagingContext"
import styles from "./FollowStar.module.scss"
import type { FollowResponse } from "../../../store/followSlice"
import type { EventRouter } from "../types"

const handleLoggedOut = (username: string) => {
    window.location.href = `/auth/login/?next=/${username}/`
}

const defaultProps = {
    position: Position.top,
    preset: Preset.dark,
    showIcon: false,
    className: "toastContained",
}

const createAfterFollow = (
    messagingInstanceId: string,
    followingEvent: EventRouter<IFollowing>,
) => {
    return (username: string, response: FollowResponse) => {
        toast.fire(
            {
                ...defaultProps,
                message: t`Following ${username}`,
            },
            `${messagingInstanceId}`,
        )
        addPageAction("DmFollow", {
            followAction: "follow",
        })

        const payload: IFollowing = {
            roomName: username,
            following: true,
            followNotificationFrequency: parseFollowNotificationFrequency(
                response.notification_frequency,
            ),
            onlineCount: response.online_count,
            totalCount: response.total_count,
        }
        followingEvent.fire(payload)
    }
}

const createAfterUnfollow = (
    messagingInstanceId: string,
    followingEvent: EventRouter<IFollowing>,
) => {
    return (username: string, response: FollowResponse) => {
        toast.fire(
            {
                ...defaultProps,
                message: t`No longer following ${username}`,
            },
            `${messagingInstanceId}`,
        )
        addPageAction("DmFollow", {
            followAction: "unfollow",
        })

        const payload: IFollowing = {
            roomName: username,
            following: false,
            followNotificationFrequency: FollowNotificationFrequency.NONE,
            onlineCount: response.online_count,
            totalCount: response.total_count,
        }
        followingEvent.fire(payload)
    }
}

const onFail = () => {
    toast.fire({
        message: t`Something went wrong. Reload the page and try again.`,
        position: Position.top,
        preset: Preset.dark,
        showIcon: false,
    })
}

export const MessageFollowStar = ({
    username,
    useCustomTooltip,
}: {
    username: string
    useCustomTooltip: boolean
}) => {
    const { followingEvent, messagingInstanceId } = useMessagingContext()
    const [tooltipText, setTooltipText] = useState<string>()

    const afterFollow = createAfterFollow(
        messagingInstanceId || GLOBAL_ID,
        followingEvent,
    )
    const afterUnfollow = createAfterUnfollow(
        messagingInstanceId || GLOBAL_ID,
        followingEvent,
    )

    const stateChangeHandler = (state: string | undefined) => {
        setTooltipText(state)
    }

    if (useCustomTooltip) {
        return (
            <ClickableTooltip
                clickToOpen={false}
                text={tooltipText}
                toolTipClassName={styles.tooltipOverride}
                placement={Placement.topMiddle}
            >
                <FollowStar
                    username={username}
                    afterFollow={afterFollow}
                    afterUnfollow={afterUnfollow}
                    handleLoggedOut={handleLoggedOut}
                    onFail={onFail}
                    customTooltipHandler={stateChangeHandler}
                />
            </ClickableTooltip>
        )
    } else {
        return (
            <FollowStar
                username={username}
                afterFollow={afterFollow}
                afterUnfollow={afterUnfollow}
                handleLoggedOut={handleLoggedOut}
                onFail={onFail}
            />
        )
    }
}
